import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../../Component/Button/Button";
import {
  belongsToFamily,
  ProductFamily,
  TaskmapHistoryEntry,
} from "../../../../API/taskmapConfigHistory";
import { useTranslation } from "react-i18next";
import { formatBlossomRange, getActiveZones } from "./taskmapFormatters";

type Props = {
  open: boolean;
  onClose: () => void;
  product: ProductFamily;
  history: TaskmapHistoryEntry[];
  onDownloadAgain: (entry: TaskmapHistoryEntry) => void;
};

function formatDateTime(isoDateTime: string, locale: string): string {
  return new Date(isoDateTime).toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function TaskmapConfigHistoryModal({
  open,
  onClose,
  product,
  history,
  onDownloadAgain,
}: Props): React.ReactNode {
  const { t, i18n } = useTranslation();
  const isVigour = product === "vigour";
  const entries = history.filter((entry) =>
    belongsToFamily(entry.product_sku, product),
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("heading.prescription_map_history")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        <List sx={{ pt: 0 }}>
          {entries.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem sx={{ py: 2, px: 3 }}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(item.created_at, i18n.language)}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      {isVigour ? (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ display: "block" }}
                        >
                          {getActiveZones(item)
                            .map(
                              (zone) =>
                                `${t("taskmap.label.class", { n: zone.lower })}: ${zone.rate}%`,
                            )
                            .join(", ") || "N/A"}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ display: "block" }}
                        >
                          {t("label.blossom_range")}
                          {": "}
                          {formatBlossomRange(item)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onDownloadAgain(item)}
                >
                  {t("button.download_again")}
                </Button>
              </ListItem>
              {index < entries.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
